/**
 * "Recently opened databases": a small, bounded, per-user list kept in localStorage - only database
 * ids and timestamps (no names, no content). It is a convenience, never proof of access: the
 * catalogs show an entry only when the user's CURRENT database list contains it.
 */
const MAX_RECENT = 30;

interface RecentEntry {
  id: string;
  at: number;
}

const keyFor = (userId: string) => `zadok.recentDatabases.${userId}`;

function read(userId: string): RecentEntry[] {
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(keyFor(userId)) ?? '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (entry): entry is RecentEntry =>
        typeof entry === 'object' &&
        entry !== null &&
        typeof (entry as RecentEntry).id === 'string' &&
        typeof (entry as RecentEntry).at === 'number',
    );
  } catch {
    return [];
  }
}

function write(userId: string, entries: RecentEntry[]): void {
  try {
    window.localStorage.setItem(keyFor(userId), JSON.stringify(entries.slice(0, MAX_RECENT)));
  } catch {
    /* storage unavailable: the list just is not remembered */
  }
}

/** Moves the database to the front (no duplicates, bounded). */
export function recordRecentDatabase(userId: string, databaseId: string): void {
  const rest = read(userId).filter((entry) => entry.id !== databaseId);
  write(userId, [{ id: databaseId, at: Date.now() }, ...rest]);
}

/** Database ids, most recent first. */
export function readRecentDatabaseIds(userId: string): string[] {
  return read(userId)
    .sort((a, b) => b.at - a.at)
    .map((entry) => entry.id);
}
